import { useSyncExternalStore } from 'react';

import { type NotificationNavigationTarget } from '@apps/web/lib/notification_navigation';

type NotificationEntryInput = {
    readonly id: string;
    readonly type: string;
    readonly notificationType: string | null;
    readonly title: string;
    readonly message: string;
    readonly resourceType: string;
    readonly resourceId: string;
    readonly timestamp: string;
    readonly mode: string | null;
    readonly navigationTarget: NotificationNavigationTarget | null;
    readonly isDismissed?: boolean;
    readonly isRead?: boolean;
};

type NotificationEntry = NotificationEntryInput & {
    readonly isDismissed: boolean;
    readonly isRead: boolean;
};

const MAX_NOTIFICATIONS = 50;

let notifications: Array<NotificationEntry> = [];
const listeners = new Set<() => void>();

function getUnreadCount(): number {
    return notifications.filter(
        (notification) => !notification.isDismissed && !notification.isRead,
    ).length;
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

function sortNotifications(
    entries: ReadonlyArray<NotificationEntry>,
): Array<NotificationEntry> {
    return [...entries]
        .sort((left, right) => {
            const timestampDiff =
                new Date(right.timestamp).getTime() -
                new Date(left.timestamp).getTime();
            if (timestampDiff !== 0) {
                return timestampDiff;
            }

            return right.id.localeCompare(left.id);
        })
        .slice(0, MAX_NOTIFICATIONS);
}

function mergeNotificationEntry(
    existingEntry: NotificationEntry | undefined,
    incomingEntry: NotificationEntryInput,
): NotificationEntry {
    return {
        ...existingEntry,
        ...incomingEntry,
        isDismissed: Boolean(
            existingEntry?.isDismissed || incomingEntry.isDismissed,
        ),
        isRead: Boolean(existingEntry?.isRead || incomingEntry.isRead),
    };
}

export const notificationStore = {
    upsertNotification(entry: NotificationEntryInput) {
        const existingEntry = notifications.find(
            (notification) => notification.id === entry.id,
        );
        const nextEntry = mergeNotificationEntry(existingEntry, entry);
        const isNewEntry = existingEntry === undefined;

        if (isNewEntry) {
            notifications = sortNotifications([nextEntry, ...notifications]);
            emitChange();
            return true;
        }

        const didChange =
            JSON.stringify(existingEntry) !== JSON.stringify(nextEntry);
        if (!didChange) {
            return false;
        }

        notifications = sortNotifications(
            notifications.map((notification) =>
                notification.id === entry.id ? nextEntry : notification,
            ),
        );
        emitChange();
        return false;
    },
    mergeNotifications(entries: ReadonlyArray<NotificationEntryInput>) {
        let didChange = false;

        for (const entry of entries) {
            const existingEntry = notifications.find(
                (notification) => notification.id === entry.id,
            );
            const nextEntry = mergeNotificationEntry(existingEntry, entry);

            if (existingEntry === undefined) {
                notifications = [nextEntry, ...notifications];
                didChange = true;
                continue;
            }

            if (JSON.stringify(existingEntry) === JSON.stringify(nextEntry)) {
                continue;
            }

            notifications = notifications.map((notification) =>
                notification.id === entry.id ? nextEntry : notification,
            );
            didChange = true;
        }

        if (!didChange) {
            return;
        }

        notifications = sortNotifications(notifications);
        emitChange();
    },
    dismissNotification(notificationId: string) {
        const notificationIndex = notifications.findIndex(
            (notification) => notification.id === notificationId,
        );
        if (notificationIndex === -1) {
            return false;
        }

        const notification = notifications[notificationIndex];
        if (notification?.isDismissed) {
            return false;
        }

        notifications = notifications.map((entry) =>
            entry.id === notificationId
                ? {
                      ...entry,
                      isDismissed: true,
                      isRead: true,
                  }
                : entry,
        );
        emitChange();
        return true;
    },
    applyNotificationStateUpdates(
        updates: ReadonlyArray<{
            readonly notificationId: string;
            readonly isRead: boolean;
            readonly isDismissed: boolean;
        }>,
    ) {
        if (updates.length === 0) {
            return;
        }

        const updatesById = new Map(
            updates.map((update) => [update.notificationId, update]),
        );
        let didChange = false;

        notifications = notifications.map((notification) => {
            const update = updatesById.get(notification.id);
            if (update === undefined) {
                return notification;
            }

            if (
                notification.isRead === update.isRead &&
                notification.isDismissed === update.isDismissed
            ) {
                return notification;
            }

            didChange = true;
            return {
                ...notification,
                isRead: update.isRead,
                isDismissed: update.isDismissed,
            };
        });

        if (!didChange) {
            return;
        }

        emitChange();
    },
    markNotificationsRead(notificationIds: ReadonlyArray<string>) {
        if (notificationIds.length === 0) {
            return;
        }

        const notificationIdSet = new Set(notificationIds);
        let didChange = false;

        notifications = notifications.map((notification) => {
            if (
                notification.isDismissed ||
                notification.isRead ||
                !notificationIdSet.has(notification.id)
            ) {
                return notification;
            }

            didChange = true;
            return {
                ...notification,
                isRead: true,
            };
        });

        if (!didChange) {
            return;
        }

        emitChange();
    },
    markAllRead() {
        if (getUnreadCount() === 0) {
            return;
        }

        notifications = notifications.map((notification) =>
            notification.isDismissed || notification.isRead
                ? notification
                : {
                      ...notification,
                      isRead: true,
                  },
        );
        emitChange();
    },
    clearAll() {
        notifications = [];
        emitChange();
    },
    getNotifications(): Array<NotificationEntry> {
        return notifications;
    },
    getUnreadCount(): number {
        return getUnreadCount();
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function useNotifications(): Array<NotificationEntry> {
    return useSyncExternalStore(
        notificationStore.subscribe,
        notificationStore.getNotifications,
    );
}

export function useUnreadCount(): number {
    return useSyncExternalStore(
        notificationStore.subscribe,
        notificationStore.getUnreadCount,
    );
}

export type { NotificationEntry, NotificationEntryInput };
